<template>
  <div
    v-if="!tabs?.length"
    data-testid="no-fields"
  >
    No fields were detected.
  </div>
  <div
    v-else
    class="refresh"
  >
    <TabTray
      :tabs="tabs"
      @tab-selected="activeTabId = $event"
    />
    <div class="refresh-wrapper">
      <div
        class="py-2"
        style="margin-left: 33px;"
      >
        <v-btn
          variant="plain"
          color="primary"
          class="text-decoration-underline opacity-100"
          @click="updateAllSelections(tabContent, true)"
        >
          Select All
        </v-btn>
        <v-btn
          variant="plain"
          color="primary"
          class="text-decoration-underline opacity-100"
          @click="updateAllSelections(tabContent, false)"
        >
          Clear
        </v-btn>
      </div>
      <MapFieldSettingOption
        v-if="tabContent"
        :tab-content="tabContent"
        @update-setting="updateSetting"
      />
    </div>
  </div>
</template>

<script lang="ts">
// Components
import TabTray from '@/components/Reusable/TabTray/TabTray.vue';
// Helpers
import MapFieldSettingOption from '@/components/MapFieldSettings/MapFieldSettingOption.vue';
import {PropType} from 'vue';
import {RefreshFieldSetting, RefreshFieldSettings, RefreshTab} from '@/services/RefreshService/types';

export default {
  name: 'RefreshFeatureSettings',
  components: {
    TabTray,
    MapFieldSettingOption,
  },
  props: {
    refreshFieldSettings: {
      type: Map as PropType<RefreshFieldSettings>,
      required: true,
    },
    collections: {
      type: Array,
      required: true,
    },
    tabs: {
      type: Array as PropType<RefreshTab[]>,
      required: false,
      default: () => [],
    },
  },
  emits: ['setSettings'],
  data() {
    return {
      activeTabId: null as string | null,
    };
  },
  computed: {
    tabContent(): RefreshFieldSetting[] {
      if (!this.activeTabId) return [];
      return this.refreshFieldSettings.get(this.activeTabId) || [];
    },
  },
  mounted() {
    if (this.tabs.length) {
      this.activeTabId = this.tabs[0]?.id || '';
    }
  },
  methods: {
    /**
     * Updates the setting at a dotted label path, its children, and its parents' indeterminate state.
     */
    updateSetting({label, value}: {label: string; value: boolean}) {
      if (!this.activeTabId) return;
      const path = label.split('.');
      let settings = this.refreshFieldSettings.get(this.activeTabId);
      for (const [index, nestedLabel] of path.entries()) {
        const setting = settings?.find((s) => s.label === nestedLabel);
        if (!setting) return;
        if (index < path.length - 1) {
          settings = setting.children;
          continue;
        }
        setting.value = value;
        if (setting.children) {
          this.updateAllSelections(setting.children, value);
        }
      }
      this.updateParentIndeterminateState(path);
    },
    /**
     * Updates the parent setting to be indeterminate if its children are not all true or all false.
     */
    updateParentIndeterminateState(nestedLabels: string[]) {
      const updateParent = (labels: string[]) => {
        if (labels.length <= 1) return;
        const parentSetting = this.tabContent.find((setting) => setting.label === labels[0]);
        const childValues = parentSetting?.children?.map((child) => child.value);
        if (!parentSetting || !childValues) return;

        if (childValues.every((val) => val === true)) {
          parentSetting.value = true;
        } else if (childValues.every((val) => val === false)) {
          parentSetting.value = false;
        } else {
          parentSetting.value = 'indeterminate';
        }
        updateParent(labels.slice(0, -1));
      };
      updateParent(nestedLabels);
    },
    updateAllSelections(settings: RefreshFieldSetting[], selected: boolean | 'indeterminate') {
      if (!settings) return;

      function selectAllSettings(settings: RefreshFieldSetting[]) {
        settings.forEach((setting) => {
          // Update the value of the setting and recursively update the children settings
          setting.value = selected;
          if (setting.children) {
            selectAllSettings(setting.children);
          }
        });
      }
      selectAllSettings(settings);
    },
  },
};
</script>

<style lang="scss" scoped>
.refresh {
  height: 355px;

  &-wrapper {
    height: 315px;
    overflow-y: scroll;
    padding-bottom: 10px;
    background-color: rgb(242, 242, 242, 0.5);
  }
}
</style>
